import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Folder,
  FileText,
  Image,
  Music,
  Video,
  File,
  Grid3X3,
  List,
  ChevronRight,
  Plus,
  Upload,
  MoreVertical,
  Trash2,
  Download,
  Share2,
  History,
  AlertTriangle,
  X,
  Search,
  ChevronLeft,
  Pencil,
  FolderInput,
} from 'lucide-react';
import api from '../api/client';
import type { AxiosResponse } from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useProject } from '../hooks/useProject';
import { useRealtimeFiles } from '../hooks/useRealtime';
import { hasPermission } from '../lib/permissions';
import { ListSkeleton } from '../components/common/ListSkeleton';
import { EmptyState } from '../components/common/EmptyState';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import FilePreview from '../components/files/FilePreview';
import UploadModal from '../components/files/UploadModal';
import ShareModal from '../components/files/ShareModal';
import type { FileItem, FileFolder } from '../types';

interface FileVersion {
  id: number;
  version_number: number;
  file_size: number;
  uploaded_by?: { id: number; name: string } | null;
  created_at: string;
}

const FILE_ICONS: Record<string, React.ElementType> = {
  'image/': Image,
  'video/': Video,
  'audio/': Music,
  'application/pdf': FileText,
  'text/': FileText,
};

function getFileIcon(mime: string) {
  for (const [prefix, Icon] of Object.entries(FILE_ICONS)) {
    if (mime.startsWith(prefix)) return Icon;
  }
  return File;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function getBackendError(err: unknown): string | null {
  const e = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
  if (e.response?.data?.message) return e.response.data.message;
  if (e.response?.data?.errors) {
    return Object.values(e.response.data.errors).flat().join(' ');
  }
  return null;
}

export default function FilesPage() {
  const { user } = useAuth();
  const { selectedProject } = useProject();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [folders, setFolders] = useState<FileFolder[]>([]);
  const [folderStack, setFolderStack] = useState<FileFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: FileItem | FileFolder; type: 'file' | 'folder' } | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [shareItem, setShareItem] = useState<{ item: FileItem | FileFolder; type: 'file' | 'folder' } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [uploadInitialFiles, setUploadInitialFiles] = useState<File[]>([]);
  const [renameModal, setRenameModal] = useState<{ item: FileItem | FileFolder; type: 'file' | 'folder'; value: string } | null>(null);
  const [moveModal, setMoveModal] = useState<{ file: FileItem; targetFolderId: number | null } | null>(null);
  const [versionsPanel, setVersionsPanel] = useState<{ file: FileItem; versions: FileVersion[] } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ message: string; onConfirm: () => Promise<void> } | null>(null);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const runDeleteConfirm = async () => {
    if (!deleteConfirm || deleteConfirmLoading) return;
    setDeleteConfirmLoading(true);
    try {
      await deleteConfirm.onConfirm();
    } finally {
      setDeleteConfirm(null);
      setDeleteConfirmLoading(false);
    }
  };

  const longPressTimerRef = useRef<number | null>(null);
  const longPressItemRef = useRef<{ item: FileItem | FileFolder; type: 'file' | 'folder' } | null>(null);
  const longPressStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const longPressTriggeredRef = useRef(false);

  const currentFolder = folderStack.length > 0 ? folderStack[folderStack.length - 1] : null;

  const isAdmin = user?.role === 'admin' || user?.role === 'yonetici';

  const canManageFile = (file: FileItem): boolean => {
    if (isAdmin) return true;
    if (file.owner?.id === user?.id) return true;
    return file.permissions?.some((p) => p.user_id === user?.id && p.can_write) ?? false;
  };

  const canDeleteFile = (file: FileItem): boolean => {
    if (isAdmin) return true;
    if (file.owner?.id === user?.id) return true;
    return file.permissions?.some((p) => p.user_id === user?.id && p.can_delete) ?? false;
  };

  const canShareFile = (file: FileItem): boolean => {
    if (isAdmin) return true;
    if (file.owner?.id === user?.id) return true;
    return file.permissions?.some((p) => p.user_id === user?.id && p.can_share) ?? false;
  };

  const canDeleteFolder = (folder: FileFolder): boolean => {
    if (isAdmin) return true;
    if (folder.owner?.id === user?.id) return true;
    return folder.permissions?.some((p) => p.user_id === user?.id && p.can_delete) ?? false;
  };

  const canManageFolder = (folder: FileFolder): boolean => {
    if (isAdmin) return true;
    if (folder.owner?.id === user?.id) return true;
    return folder.permissions?.some((p) => p.user_id === user?.id && p.can_write) ?? false;
  };

  const canShareFolder = (folder: FileFolder): boolean => {
    if (isAdmin) return true;
    if (folder.owner?.id === user?.id) return true;
    return folder.permissions?.some((p) => p.user_id === user?.id && p.can_share) ?? false;
  };

  const canCreateHere = currentFolder ? canManageFolder(currentFolder) : hasPermission(user, 'files', 'create');
  const canUploadHere = currentFolder ? canManageFolder(currentFolder) : hasPermission(user, 'files', 'upload');

  const fetchData = useCallback(async (targetPage?: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedProject) params.append('project_id', String(selectedProject.id));
      if (currentFolder) params.append('folder_id', String(currentFolder.id));
      if (search) params.append('search', search);
      params.append('page', String(targetPage ?? page));
      params.append('per_page', String(perPage));

      const fileRes = await api.get(`/api/v1/files?${params.toString()}`, { signal });
      const payload = fileRes.data?.data ?? { folders: [], files: [] };
      setFiles((payload.files || []) as FileItem[]);
      setFolders((payload.folders || []) as FileFolder[]);
      setHasMore((payload.files || []).length === perPage);
    } catch (err: unknown) {
      const e = err as { name?: string; response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } } };
      if (e.name === 'CanceledError' || e.name === 'AbortError') {
        return;
      }
      console.error('Hata:', err);
      const backendMessage = e.response?.data?.message;
      const status = e.response?.status;
      setError(
        backendMessage
          ? `Hata ${status ? `(${status})` : ''}: ${backendMessage}`
          : 'Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedProject, currentFolder, search, page, perPage]);

  useEffect(() => {
    setPage(1);
  }, [selectedProject, currentFolder]);

  useEffect(() => {
    const timer = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSelectedItems(new Set());
  }, [currentFolder, search, page, selectedProject]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(undefined, controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  useRealtimeFiles(selectedProject?.id, {
    onFileCreated: (file, folderId) => {
      if (folderId !== currentFolder?.id) return;
      setFiles((prev) => (prev.some((f) => f.id === file.id) ? prev : [...prev, file]));
    },
    onFileUpdated: (file) => {
      setFiles((prev) =>
        prev.map((f) => {
          if (f.id !== file.id) return f;
          return file.folder_id === currentFolder?.id ? file : f;
        })
      );
    },
    onFileDeleted: (fileId) => {
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    },
    onFolderCreated: (folder, parentId) => {
      if (parentId !== currentFolder?.id) return;
      setFolders((prev) => (prev.some((f) => f.id === folder.id) ? prev : [...prev, folder]));
    },
    onFolderUpdated: (folder) => {
      setFolders((prev) => {
        const inCurrent = folder.parent_id === currentFolder?.id;
        const exists = prev.some((f) => f.id === folder.id);
        if (exists) {
          return prev.map((f) => (f.id === folder.id ? folder : f));
        }
        return inCurrent ? [...prev, folder] : prev;
      });
    },
    onFolderDeleted: (folderId) => {
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      if (folderId === currentFolder?.id) {
        setFolderStack((prev) => prev.slice(0, -1));
      }
    },
    onFilePermissionUpdated: (itemId, itemType, permission, deleted) => {
      if (itemType === 'file') {
        setFiles((prev) =>
          prev.map((f) => {
            if (f.id !== itemId) return f;
            const perms = f.permissions || [];
            if (deleted || !permission) {
              return { ...f, permissions: perms.filter((p) => p.user_id !== (permission?.user_id ?? -1)) };
            }
            const idx = perms.findIndex((p) => p.user_id === permission.user_id);
            const updated = idx >= 0 ? perms.map((p, i) => (i === idx ? permission : p)) : [...perms, permission];
            return { ...f, permissions: updated };
          })
        );
      } else {
        setFolders((prev) =>
          prev.map((f) => {
            if (f.id !== itemId) return f;
            const perms = f.permissions || [];
            if (deleted || !permission) {
              return { ...f, permissions: perms.filter((p) => p.user_id !== (permission?.user_id ?? -1)) };
            }
            const idx = perms.findIndex((p) => p.user_id === permission.user_id);
            const updated = idx >= 0 ? perms.map((p, i) => (i === idx ? permission : p)) : [...perms, permission];
            return { ...f, permissions: updated };
          })
        );
      }
    },
  });

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    try {
      await api.post('/api/v1/file-folders', {
        name: newFolderName.trim(),
        parent_id: currentFolder?.id || null,
        project_id: selectedProject?.id || null,
      });
      setNewFolderName('');
      setShowNewFolder(false);
      fetchData();
    } catch (err) {
      console.error('Hata:', err);
      setError(getBackendError(err) || 'Klasör oluşturulurken bir hata oluştu.');
    }
  };

  const handleDelete = () => {
    if (!contextMenu) return;
    const target = contextMenu;
    setDeleteConfirm({
      message: 'Silmek istediğinize emin misiniz?',
      onConfirm: async () => {
        try {
          if (target.type === 'file') {
            await api.delete(`/api/v1/files/${target.item.id}`);
          } else {
            await api.delete(`/api/v1/file-folders/${target.item.id}`);
          }
          fetchData();
          setContextMenu(null);
        } catch (err) {
          console.error('Hata:', err);
          setError(getBackendError(err) || 'Silme işlemi sırasında bir hata oluştu.');
          setContextMenu(null);
        }
      },
    });
  };

  const openFolder = (folder: FileFolder) => {
    setFolderStack((prev) => [...prev, folder]);
  };

  const navigateBreadcrumb = (index: number) => {
    setFolderStack((prev) => prev.slice(0, index));
  };

  const storageUsed = user?.storage_used || 0;
  const storageLimit = user?.storage_limit || 1;
  const storagePercent = Math.min(100, Math.round((storageUsed / storageLimit) * 100));

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (!canUploadHere) {
      return;
    }
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      setUploadInitialFiles(droppedFiles);
      setUploadOpen(true);
    }
  };

  const startLongPress = (
    item: FileItem | FileFolder,
    type: 'file' | 'folder',
    e: React.TouchEvent | React.MouseEvent,
  ) => {
    longPressTriggeredRef.current = false;
    longPressItemRef.current = { item, type };
    const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY;
    longPressStartPosRef.current = { x: clientX, y: clientY };

    if ('touches' in e) {
      e.preventDefault();
    }

    if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      if (longPressItemRef.current) {
        const { item: pressedItem, type: pressedType } = longPressItemRef.current;
        setContextMenu({ x: clientX ?? 0, y: clientY ?? 0, item: pressedItem, type: pressedType });
      }
    }, 500);
  };

  const moveLongPress = (e: React.TouchEvent | React.MouseEvent) => {
    if (!longPressStartPosRef.current || longPressTimerRef.current === null) return;
    const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY;
    const dx = clientX - longPressStartPosRef.current.x;
    const dy = clientY - longPressStartPosRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > 10) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      longPressItemRef.current = null;
    }
  };

  const endLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleCardClick = (e: React.MouseEvent, action: () => void) => {
    if (longPressTriggeredRef.current) {
      e.preventDefault();
      e.stopPropagation();
      longPressTriggeredRef.current = false;
      return;
    }
    action();
  };

  const handleDownload = async (file: FileItem) => {
    try {
      const response = await api.get(`/api/v1/files/${file.id}/download`, {
        responseType: 'blob',
      });

      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Hata:', err);
      setError(getBackendError(err) || 'Dosya indirilirken bir hata oluştu.');
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleZipResponse = async (response: AxiosResponse<Blob>) => {
    const contentType = String(response.headers['content-type'] || '');
    if (contentType.includes('application/json')) {
      try {
        const text = await response.data.text();
        const json = JSON.parse(text);
        setError(json.message || 'İndirme paketi hazırlanıyor. Hazır olduğunda bildirim alacaksınız.');
      } catch {
        setError('İndirme paketi hazırlanıyor. Hazır olduğunda bildirim alacaksınız.');
      }
      return;
    }
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : 'indirme.zip';
    downloadBlob(response.data, filename);
  };

  const handleDownloadFolder = async (folder: FileFolder) => {
    try {
      const response = await api.get(`/api/v1/file-folders/${folder.id}/download`, {
        responseType: 'blob',
      });
      await handleZipResponse(response);
    } catch (err) {
      console.error('Hata:', err);
      setError(getBackendError(err) || 'Klasör indirilirken bir hata oluştu.');
    }
  };

  const handleBulkDownload = async () => {
    if (selectedItems.size === 0) return;
    const fileIds: number[] = [];
    const folderIds: number[] = [];
    selectedItems.forEach((key) => {
      const [type, id] = key.split(':');
      if (type === 'file') fileIds.push(Number(id));
      if (type === 'folder') folderIds.push(Number(id));
    });

    try {
      const response = await api.post(
        '/api/v1/files/download/bulk',
        { file_ids: fileIds, folder_ids: folderIds },
        { responseType: 'blob' }
      );
      await handleZipResponse(response);
      setSelectedItems(new Set());
    } catch (err) {
      console.error('Hata:', err);
      setError(getBackendError(err) || 'Seçili öğeler indirilirken bir hata oluştu.');
    }
  };

  const toggleSelection = (type: 'file' | 'folder', id: number) => {
    const key = `${type}:${id}`;
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearSelection = () => setSelectedItems(new Set());

  const isSelected = (type: 'file' | 'folder', id: number) => selectedItems.has(`${type}:${id}`);

  const handleRenameSave = async () => {
    if (!renameModal || !renameModal.value.trim()) return;
    try {
      if (renameModal.type === 'file') {
        await api.put(`/api/v1/files/${renameModal.item.id}`, { name: renameModal.value.trim() });
      } else {
        await api.put(`/api/v1/file-folders/${renameModal.item.id}`, { name: renameModal.value.trim() });
      }
      setRenameModal(null);
      fetchData();
    } catch (err) {
      console.error('Hata:', err);
      setError(getBackendError(err) || 'Yeniden adlandırma sırasında bir hata oluştu.');
    }
  };

  const handleMoveSave = async () => {
    if (!moveModal || !moveModal.targetFolderId) return;
    try {
      await api.put(`/api/v1/files/${moveModal.file.id}`, {
        name: moveModal.file.name,
        folder_id: moveModal.targetFolderId,
      });
      setMoveModal(null);
      fetchData();
    } catch (err) {
      console.error('Hata:', err);
      setError(getBackendError(err) || 'Dosya taşınırken bir hata oluştu.');
    }
  };

  const loadVersions = async (file: FileItem) => {
    try {
      const res = await api.get(`/api/v1/files/${file.id}/versions`);
      setVersionsPanel({ file, versions: (res.data?.data || []) as FileVersion[] });
    } catch (err) {
      console.error('Hata:', err);
      setError(getBackendError(err) || 'Versiyonlar yüklenirken bir hata oluştu.');
    }
  };

  const handleUploadVersion = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!versionsPanel || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/api/v1/files/${versionsPanel.file.id}/versions`, formData);
      await loadVersions(versionsPanel.file);
    } catch (err) {
      console.error('Hata:', err);
      setError(getBackendError(err) || 'Yeni versiyon yüklenirken bir hata oluştu.');
    } finally {
      e.target.value = '';
    }
  };

  const handleDeleteVersion = (version: FileVersion) => {
    if (!versionsPanel) return;
    const file = versionsPanel.file;
    setDeleteConfirm({
      message: 'Bu versiyonu silmek istediğinize emin misiniz?',
      onConfirm: async () => {
        try {
          await api.delete(`/api/v1/files/${file.id}/versions/${version.id}`);
          await loadVersions(file);
        } catch (err) {
          console.error('Hata:', err);
          setError(getBackendError(err) || 'Versiyon silinirken bir hata oluştu.');
        }
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Kota uyarısı */}
      <div data-testid="storage-quota" className="rounded-xl bg-white p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Depolama Kotası</span>
          <span
            data-testid="storage-percentage"
            className={`text-sm font-semibold ${storagePercent >= 90 ? 'text-red-600' : storagePercent >= 80 ? 'text-orange-600' : 'text-slate-900'}`}
          >
            {storagePercent}%
          </span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className={`h-full rounded-full transition-all ${
              storagePercent >= 90 ? 'bg-red-500' : storagePercent >= 80 ? 'bg-orange-500' : 'bg-blue-500'
            }`}
            style={{ width: `${storagePercent}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {formatSize(storageUsed)} / {formatSize(storageLimit)}
        </p>
        {storagePercent >= 80 && (
          <div data-testid="storage-warning" className="mt-2 flex items-center gap-1.5 text-xs font-medium text-orange-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            Depolama alanınız azalıyor.
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => fetchData()}
            data-testid="files-error-retry"
            className="shrink-0 rounded-lg border border-red-300 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Tekrar Dene
          </button>
        </div>
      )}

      {/* Üst bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          {/* Breadcrumb */}
          <button
            data-testid="breadcrumb-root"
            onClick={() => navigateBreadcrumb(0)}
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Dosyalar
          </button>
          {folderStack.map((f, idx) => (
            <div key={f.id} className="flex min-w-0 items-center gap-1">
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
              <button
                data-testid={`breadcrumb-folder-${f.id}`}
                onClick={() => navigateBreadcrumb(idx + 1)}
                className="max-w-[120px] truncate text-sm font-medium text-slate-600 hover:text-slate-900"
                title={f.name}
              >
                {f.name}
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-2 top-2 h-4 w-4 text-slate-500" />
            <input
              data-testid="file-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ara..."
              className="w-full rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm outline-none focus:border-blue-500 sm:w-56"
            />
          </div>

          <div className="flex shrink-0 rounded-lg border border-slate-200 bg-white">
            <button
              data-testid="view-mode-grid"
              onClick={() => setViewMode('grid')}
              className={`rounded-l-lg p-1.5 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              data-testid="view-mode-list"
              onClick={() => setViewMode('list')}
              className={`rounded-r-lg p-1.5 ${viewMode === 'list' ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {canCreateHere && (
            <button
              data-testid="new-folder-button"
              onClick={() => setShowNewFolder(!showNewFolder)}
              className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Plus className="h-4 w-4" />
              Yeni Klasör
            </button>
          )}

          {canUploadHere && (
            <button
              data-testid="upload-button"
              onClick={() => setUploadOpen(true)}
              className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <Upload className="h-4 w-4" />
              Yükle
            </button>
          )}

          {selectedItems.size > 0 && (
            <>
              <button
                data-testid="bulk-download-button"
                onClick={handleBulkDownload}
                className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg bg-green-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-green-700"
              >
                <Download className="h-4 w-4" />
                Seçili ({selectedItems.size})
              </button>
              <button
                data-testid="clear-selection-button"
                onClick={clearSelection}
                className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
                Temizle
              </button>
            </>
          )}
        </div>
      </div>

      {/* Yeni Klasör Formu */}
      {showNewFolder && (
        <form onSubmit={handleCreateFolder} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
          <Folder className="h-5 w-5 text-yellow-500" />
          <input
            data-testid="folder-name-input"
            autoFocus
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Klasör adı"
            className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-blue-500"
          />
          <button
            data-testid="save-folder-button"
            type="submit"
            disabled={!newFolderName.trim()}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Oluştur
          </button>
          <button
            type="button"
            onClick={() => setShowNewFolder(false)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
          >
            <X className="h-4 w-4" />
          </button>
        </form>
      )}

      {/* Drag & Drop Zone */}
      <div
        ref={dragRef}
        data-testid="files-drop-zone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`hidden sm:block rounded-xl border-2 border-dashed p-6 text-center transition ${
          dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white'
        }`}
      >
        {dragOver ? (
          <p className="text-sm font-medium text-blue-600">Dosyaları buraya bırakın</p>
        ) : (
          <p className="text-sm text-slate-500">Dosyaları sürükleyip bırakabilir veya "Yükle" butonunu kullanabilirsiniz.</p>
        )}
      </div>

      {/* İçerik */}
      {loading && folders.length === 0 && files.length === 0 ? (
        <ListSkeleton variant="card" rows={8} testId="files-skeleton" />
      ) : !error && folders.length === 0 && files.length === 0 ? (
        <EmptyState icon={Folder} title="Klasör boş" description="Bu klasörde dosya veya alt klasör yok." testId="files-empty-state" />
      ) : (
        <>
          <div
            data-testid="file-list"
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
                : 'flex flex-col gap-2'
            }
          >
            {/* Klasörler */}
            {folders.map((folder) => (
              <div
                key={folder.id}
                data-testid="folder-card"
                onClick={(e) => handleCardClick(e, () => openFolder(folder))}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, item: folder, type: 'folder' });
                }}
                onMouseDown={(e) => startLongPress(folder, 'folder', e)}
                onMouseUp={endLongPress}
                onMouseLeave={endLongPress}
                onMouseMove={moveLongPress}
                onTouchStart={(e) => startLongPress(folder, 'folder', e)}
                onTouchEnd={endLongPress}
                onTouchMove={moveLongPress}
                className={`group relative rounded-xl border border-slate-200 bg-white text-left transition hover:shadow-md ${
                  viewMode === 'grid' ? 'p-4' : 'flex items-center gap-3 p-3'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected('folder', folder.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleSelection('folder', folder.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`folder-select-${folder.id}`}
                  className={`shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 ${
                    viewMode === 'grid' ? 'absolute left-2 top-2 h-4 w-4' : 'h-4 w-4'
                  }`}
                />
                <Folder className={`shrink-0 text-yellow-500 ${viewMode === 'grid' ? 'mx-auto mb-2 h-10 w-10' : 'h-6 w-6'}`} />
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-sm font-medium text-slate-800 ${viewMode === 'grid' ? 'text-center' : ''}`}>
                    {folder.name}
                  </p>
                  {viewMode === 'list' && <p className="text-xs text-slate-500">Klasör</p>}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setContextMenu({ x: e.clientX, y: e.clientY, item: folder, type: 'folder' });
                  }}
                  data-testid={`folder-menu-${folder.id}`}
                  className="rounded p-2 text-slate-500 hover:bg-slate-100"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Dosyalar */}
            {files.map((file) => {
              const Icon = getFileIcon(file.mime_type);
              return (
                <div
                  key={file.id}
                  data-testid="file-card"
                  onClick={(e) => handleCardClick(e, () => setPreviewFile(file))}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({ x: e.clientX, y: e.clientY, item: file, type: 'file' });
                  }}
                  onMouseDown={(e) => startLongPress(file, 'file', e)}
                  onMouseUp={endLongPress}
                  onMouseLeave={endLongPress}
                  onMouseMove={moveLongPress}
                  onTouchStart={(e) => startLongPress(file, 'file', e)}
                  onTouchEnd={endLongPress}
                  onTouchMove={moveLongPress}
                  className={`group relative rounded-xl border border-slate-200 bg-white text-left transition hover:shadow-md ${
                    viewMode === 'grid' ? 'p-4' : 'flex items-center gap-3 p-3'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected('file', file.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelection('file', file.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`file-select-${file.id}`}
                    className={`shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 ${
                      viewMode === 'grid' ? 'absolute left-2 top-2 h-4 w-4' : 'h-4 w-4'
                    }`}
                  />
                  <Icon className={`shrink-0 text-blue-500 ${viewMode === 'grid' ? 'mx-auto mb-2 h-10 w-10' : 'h-6 w-6'}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`truncate text-sm font-medium text-slate-800 ${viewMode === 'grid' ? 'text-center' : ''}`}>
                      {file.original_name}
                    </p>
                    {viewMode === 'list' && (
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{formatSize(file.size)}</span>
                        <span>{new Date(file.created_at).toLocaleDateString('tr-TR')}</span>
                      </div>
                    )}
                    {viewMode === 'grid' && (
                      <p className="mt-1 text-center text-xs text-slate-500">{formatSize(file.size)}</p>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setContextMenu({ x: e.clientX, y: e.clientY, item: file, type: 'file' });
                    }}
                    data-testid={`file-menu-${file.id}`}
                    className="ml-auto rounded p-2 text-slate-500 hover:bg-slate-100"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              data-testid="pagination-previous"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Önceki
            </button>
            <span data-testid="pagination-page" className="text-sm text-slate-600">Sayfa {page}</span>
            <button
              data-testid="pagination-next"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore || loading}
              className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Sonraki
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />
          <div
            className="fixed z-50 w-48 max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
            style={{
              top: Math.max(8, Math.min(window.innerHeight - 200, contextMenu.y)),
              left: Math.max(8, Math.min(window.innerWidth - 200, contextMenu.x)),
            }}
          >
            {contextMenu.type === 'file' && canManageFile(contextMenu.item as FileItem) && (
              <button
                data-testid="file-versions-menu"
                onClick={() => {
                  setContextMenu(null);
                  void loadVersions(contextMenu.item as FileItem);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <History className="h-4 w-4" />
                Versiyonlar
              </button>
            )}

            {contextMenu.type === 'file' && canManageFile(contextMenu.item as FileItem) && (
              <button
                data-testid="rename-file-menu"
                onClick={() => {
                  const item = contextMenu.item as FileItem;
                  setRenameModal({ item, type: 'file', value: item.original_name });
                  setContextMenu(null);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Pencil className="h-4 w-4" />
                Yeniden Adlandır
              </button>
            )}

            {contextMenu.type === 'file' && canManageFile(contextMenu.item as FileItem) && (
              <button
                data-testid="move-file-menu"
                onClick={() => {
                  const item = contextMenu.item as FileItem;
                  setMoveModal({ file: item, targetFolderId: folders.length > 0 ? folders[0].id : null });
                  setContextMenu(null);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <FolderInput className="h-4 w-4" />
                Taşı
              </button>
            )}

            {contextMenu.type === 'folder' && canManageFolder(contextMenu.item as FileFolder) && (
              <button
                data-testid="rename-folder-menu"
                onClick={() => {
                  const item = contextMenu.item as FileFolder;
                  setRenameModal({ item, type: 'folder', value: item.name });
                  setContextMenu(null);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Pencil className="h-4 w-4" />
                Yeniden Adlandır
              </button>
            )}

            {(contextMenu.type === 'file' || contextMenu.type === 'folder') && (
              <button
                data-testid="context-download-button"
                onClick={() => {
                  setContextMenu(null);
                  if (contextMenu.type === 'file') {
                    handleDownload(contextMenu.item as FileItem);
                  } else {
                    handleDownloadFolder(contextMenu.item as FileFolder);
                  }
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Download className="h-4 w-4" />
                İndir
              </button>
            )}

            {((contextMenu.type === 'file' && canShareFile(contextMenu.item as FileItem)) ||
              (contextMenu.type === 'folder' && canShareFolder(contextMenu.item as FileFolder))) && (
              <button
                onClick={() => {
                  setContextMenu(null);
                  setShareItem({ item: contextMenu.item, type: contextMenu.type });
                }}
                data-testid="context-share-button"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Share2 className="h-4 w-4" />
                Paylaş
              </button>
            )}

            {((contextMenu.type === 'file' && canDeleteFile(contextMenu.item as FileItem)) ||
              (contextMenu.type === 'folder' && canDeleteFolder(contextMenu.item as FileFolder))) && (
              <button
                data-testid="context-delete-button"
                onClick={handleDelete}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Sil
              </button>
            )}
          </div>
        </>
      )}

      {/* Preview */}
      {previewFile && (
        <FilePreview
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onShare={() => setShareItem({ type: 'file', item: previewFile })}
        />
      )}

      {/* Upload Modal */}
      {uploadOpen && (
        <UploadModal
          folderId={currentFolder?.id || null}
          projectId={selectedProject?.id || null}
          initialFiles={uploadInitialFiles}
          onClose={() => {
            setUploadOpen(false);
            setUploadInitialFiles([]);
          }}
          onUploaded={() => {
            setUploadOpen(false);
            setUploadInitialFiles([]);
            fetchData();
          }}
        />
      )}

      {/* Share Modal */}
      {shareItem && (
        <ShareModal
          type={shareItem.type}
          item={shareItem.item}
          onClose={() => setShareItem(null)}
          onSaved={fetchData}
        />
      )}

      {/* Rename Modal */}
      {renameModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div data-testid="rename-modal" className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold text-slate-800">
              {renameModal.type === 'file' ? 'Dosyayı Yeniden Adlandır' : 'Klasörü Yeniden Adlandır'}
            </h3>
            <input
              data-testid="rename-input"
              value={renameModal.value}
              onChange={(e) => setRenameModal({ ...renameModal, value: e.target.value })}
              className="mb-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRenameModal(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                İptal
              </button>
              <button
                data-testid="rename-save"
                onClick={handleRenameSave}
                disabled={!renameModal.value.trim()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {moveModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div data-testid="move-modal" className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold text-slate-800">Dosyayı Taşı</h3>
            {folders.length === 0 ? (
              <p className="mb-4 text-sm text-slate-500">Taşıyabileceğiniz bir klasör bulunmuyor.</p>
            ) : (
              <div className="mb-4 max-h-60 overflow-auto rounded-lg border border-slate-200">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    data-testid={`move-folder-option-${folder.id}`}
                    onClick={() => setMoveModal({ ...moveModal, targetFolderId: folder.id })}
                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-50 ${
                      moveModal.targetFolderId === folder.id ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                    }`}
                  >
                    <Folder className="h-4 w-4 text-yellow-500" />
                    {folder.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setMoveModal(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                İptal
              </button>
              <button
                data-testid="move-save"
                onClick={handleMoveSave}
                disabled={!moveModal.targetFolderId}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Taşı
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Versions Panel */}
      {versionsPanel && (
        <div className="fixed inset-0 z-[100] flex justify-end bg-black/50">
          <div data-testid="versions-panel" className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800">Versiyonlar</h3>
              <button
                onClick={() => setVersionsPanel(null)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <label className="mb-4 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100">
              <Upload className="h-4 w-4" />
              Yeni Versiyon Yükle
              <input
                data-testid="upload-new-version-input"
                type="file"
                accept="*/*"
                className="hidden"
                onChange={handleUploadVersion}
              />
            </label>
            <div className="space-y-2">
              {versionsPanel.versions.length === 0 && (
                <p className="text-center text-sm text-slate-500">Henüz versiyon bulunmuyor.</p>
              )}
              {versionsPanel.versions.map((version) => (
                <div
                  key={version.id}
                  data-testid={`version-row-${version.version_number}`}
                  className="flex items-center justify-between rounded-lg border border-slate-200 p-3"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-800">Versiyon {version.version_number}</p>
                    <p className="text-xs text-slate-500">
                      {formatSize(version.file_size)} • {new Date(version.created_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  {versionsPanel.versions.length > 1 && (
                    <button
                      data-testid={`delete-version-${version.version_number}`}
                      onClick={() => handleDeleteVersion(version)}
                      className="rounded p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {/* Silme onay dialogu (panel z-[100] üstünde kalması için panel içinde) */}
            <ConfirmDialog
              open={deleteConfirm !== null}
              title="Silme Onayı"
              description={deleteConfirm?.message}
              confirmLabel="Sil"
              variant="danger"
              loading={deleteConfirmLoading}
              onConfirm={() => void runDeleteConfirm()}
              onCancel={() => setDeleteConfirm(null)}
            />
          </div>
        </div>
      )}

      {/* Silme onay dialogu (dosya/klasör silme; versiyon paneli kapalıyken) */}
      {!versionsPanel && (
        <ConfirmDialog
          open={deleteConfirm !== null}
          title="Silme Onayı"
          description={deleteConfirm?.message}
          confirmLabel="Sil"
          variant="danger"
          loading={deleteConfirmLoading}
          onConfirm={() => void runDeleteConfirm()}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}
